import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger);

const timeline = [
  {
    period: 'Jun 2025 – Jun 2029',
    title: 'B.Tech — CSE (AI & Data Engineering)',
    org: 'Aurora Deemed to be University',
    type: 'Education',
  },
  {
    period: 'Jul 2023 – May 2025',
    title: 'Intermediate',
    org: 'Sri Viswa Junior College',
    type: 'Education',
  },
];

export default function About() {
  const container = useRef();

  useGSAP(() => {
    gsap.from('.about-anim', {
      scrollTrigger: { trigger: container.current, start: 'top 75%' },
      y: 50, opacity: 0, duration: 1, stagger: 0.15, ease: 'power3.out',
    });
  }, { scope: container });

  return (
    <section id="about" ref={container} style={{ padding: '8rem 0', borderTop: '1px solid var(--border)' }}>
      <div className="page-wrap">

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '5rem', flexWrap: 'wrap', gap: '2rem' }}>
          <div className="about-anim">
            <span className="sec-num">01</span>
            <h2 className="display-lg" style={{ marginTop: '-1.5rem' }}>About Me</h2>
          </div>
          <p className="about-anim" style={{ maxWidth: '45ch', color: 'var(--muted)', fontSize: '1.05rem', lineHeight: 1.8 }}>
            B.Tech student specializing in AI Web Development and AI Automation Engineering. I build intelligent web applications, multi-step agent workflows, and LLM-powered chatbots.
          </p>
        </div>

        <hr className="hr" style={{ marginBottom: '5rem' }} />

        {/* Two columns */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5rem' }} className="about-cols">

          {/* Left: story */}
          <div className="about-anim">
            <div className="hr-gold" />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', marginBottom: '1.5rem', color: 'var(--white)' }}>
              The Story
            </h3>
            <p style={{ color: 'var(--muted)', fontSize: '1rem', lineHeight: 1.9, marginBottom: '1.5rem' }}>
              I'm Pradeep Kumar from Visakhapatnam — a developer who loves sitting at the intersection of AI and the web. 
              My work ranges from building clean, responsive frontends in React, to designing complex multi-step AI agent workflows using n8n.
            </p>
            <p style={{ color: 'var(--muted)', fontSize: '1rem', lineHeight: 1.9, marginBottom: '2rem' }}>
              As co-founder of Trione Marketing.co, I've built and maintained a production website, handled branding strategy, and delivered real UX improvements. I believe in shipping real things, not just learning tutorials.
            </p>

            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <a href="mailto:pradeepkumarsirigirisetty@gmail.com" className="btn btn-gold" style={{ fontSize: '0.8rem', padding: '0.65rem 1.25rem' }}>
                Email Me
              </a>
              <a href="https://github.com/Pradeepkumarsirigirisetty" target="_blank" rel="noreferrer" className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.65rem 1.25rem' }}>
                GitHub
              </a>
              <a href="https://www.linkedin.com/in/sirigirisetty-pradeep-kumar-218591340/" target="_blank" rel="noreferrer" className="btn btn-outline" style={{ fontSize: '0.8rem', padding: '0.65rem 1.25rem' }}>
                LinkedIn
              </a>
            </div>
          </div>

          {/* Right: timeline */}
          <div className="about-anim">
            <div className="hr-gold" />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', marginBottom: '2rem', color: 'var(--white)' }}>
              Education
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {timeline.map((item, i) => (
                <div key={i} style={{
                  display: 'grid', gridTemplateColumns: '1px 1fr',
                  gap: '1.5rem', paddingBottom: '2.5rem',
                }}>
                  {/* Timeline line + dot */}
                  <div style={{ position: 'relative' }}>
                    <div style={{ position: 'absolute', top: '6px', left: '-3px', width: '7px', height: '7px', borderRadius: '50%', background: 'var(--gold)' }} />
                    {i < timeline.length - 1 && (
                      <div style={{ position: 'absolute', top: '20px', left: '0', width: '1px', height: '100%', background: 'var(--border)' }} />
                    )}
                  </div>
                  <div>
                    <span className="label-gold" style={{ display: 'block', marginBottom: '0.35rem' }}>{item.period}</span>
                    <h4 style={{ color: 'var(--white)', fontWeight: 600, fontSize: '1rem', marginBottom: '0.25rem' }}>{item.title}</h4>
                    <p style={{ color: 'var(--muted)', fontSize: '0.9rem', margin: 0 }}>{item.org}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact details */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '2rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <span className="label" style={{ width: '70px', flexShrink: 0 }}>Phone</span>
                <span style={{ color: 'var(--white)', fontSize: '0.9rem' }}>+91 78937 89097</span>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <span className="label" style={{ width: '70px', flexShrink: 0 }}>Location</span>
                <span style={{ color: 'var(--white)', fontSize: '0.9rem' }}>Visakhapatnam, AP</span>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <span className="label" style={{ width: '70px', flexShrink: 0 }}>Status</span>
                <span style={{ color: 'var(--gold)', fontSize: '0.9rem', fontWeight: 600 }}>Open to Internships</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .about-cols { grid-template-columns: 1fr !important; gap: 3rem !important; }
        }
      `}</style>
    </section>
  );
}
